/**
 * Priority List Governance Page
 * 
 * Manages the governance lifecycle of Priority Lists:
 * Proposed → Decided → Ratified → Published
 * 
 * The "Decided" state is the key gate - it authorizes planning to begin.
 *
 * Contest (migration 969): a ratified or published list shows who agreed
 * (ratified by, published by) and, beside it, who did not.
 */

import { useState, useMemo, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import {
  ChevronRight,
  CheckCircle,
  Clock,
  FileText,
  ArrowRight,
  Lock,
  Unlock,
  Send,
  RotateCcw,
  Flag,
} from 'lucide-react';
import { usePrioritySetStore } from '../../../stores/prioritization/prioritySetStore';
import { useDegapProcessStore } from '../../../stores/prioritization/degapProcessStore';
import { useItemsStore } from '../../../stores/prioritization/itemsStore';
import { useUserStore } from '../../../stores/prioritization/userStore';
import { InversionAcknowledgmentPanel } from '../../../components/prioritization/priorities/InversionAcknowledgmentPanel';
import {
  type PrioritySetState,
  type PriorityOverride,
  PRIORITY_SET_STATE_LABELS,
  PRIORITY_SET_STATE_COLORS,
  getPlanningEnablement,
  PLANNING_ENABLEMENT_LABELS,
} from '../../../types/prioritization/prioritySet';
import { RatificationNextSteps } from '../../../components/prioritization/catchball/RatificationNextSteps';
import { useRatificationSteps } from '../../../components/prioritization/catchball/ratificationSteps';
import { ItemRelationshipFlags } from '@/components/prioritization/ItemRelationshipFlags';
import { StructureVersionFinePrint } from '@/components/hierarchy/StructureVersionFinePrint';
import { Button } from '@/components/ui/Button';
import { WhoDidNotAgreeSection, ContestListModal } from '@/components/prioritization/priorities/PriorityListContests';
import { usePriorityListContests } from '@/hooks/usePriorityListContests';
import { didNotAgreeWords, isContestableListState } from '@/lib/contestService';
import { usePriorityListRatifyRight } from '@/hooks/prioritization/useActRight';
import { useDecisionsStore } from '../../../stores/prioritization/decisionsStore';
import { CallSentence } from '../../../components/prioritization/decisions/CallSentence';
import { decisionForList, isAgreedListState, listDecisionGapText } from '@/lib/call/rankingDecision';
import { fetchListApprovalDecisionIds } from '@/lib/call/rankingDecisionService';
import { loadInversionAcknowledgments } from '../../../lib/inversionAcknowledgmentService';
import { acknowledgedInversionsByHigherItem, type InversionAcknowledgment } from '../../../lib/prioritizationInversionDetector';
import { InversionMark } from '../../../components/prioritization/priorities/InversionMark';
import { useActorDisplayMap } from '@/hooks/prioritization/useActorDisplayMap';

// State badge component
function StateBadge({ state }: { state: PrioritySetState }) {
  return (
    <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${PRIORITY_SET_STATE_COLORS[state]}`}>
      {PRIORITY_SET_STATE_LABELS[state]}
    </span>
  );
}

// Progress steps component
function GovernanceProgress({ currentState }: { currentState: PrioritySetState }) {
  const steps: { state: PrioritySetState; label: string }[] = [
    { state: 'proposed', label: 'Proposed' },
    { state: 'decided', label: 'Decided' },
    { state: 'ratified', label: 'Ratified' },
    { state: 'published', label: 'Published' },
  ];
  
  const currentIndex = steps.findIndex(s => s.state === currentState);
  
  return (
    <nav className="flex items-center justify-center" aria-label="Progress">
      <ol className="flex items-center space-x-5">
        {steps.map((step, index) => {
          const isCompleted = index < currentIndex;
          const isCurrent = index === currentIndex;
          
          return (
            <li key={step.state} className="relative">
              {index > 0 && (
                <div className={`absolute w-5 h-0.5 -left-5 top-4 ${
                  isCompleted ? 'bg-[var(--slate)]' : 'bg-[var(--rule-bright)]'
                }`} />
              )}
              <div className={`relative flex flex-col items-center ${
                isCurrent ? 'text-[var(--text-ter)]' : isCompleted ? 'text-[var(--text-ter)]' : 'text-[var(--text-ter)]'
              }`}>
                <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                  isCompleted ? 'bg-[var(--slate)] text-white' :
                  isCurrent ? 'border-2 border-[var(--slate)] bg-[var(--bg-raised)]' :
                  'border-2 border-[var(--rule)] bg-[var(--bg-raised)]'
                }`}>
                  {isCompleted ? (
                    <CheckCircle className="h-5 w-5" />
                  ) : (
                    <span className="text-sm font-medium">{index + 1}</span>
                  )}
                </div>
                <span className="mt-2 text-xs font-medium">{step.label}</span>
              </div>
            </li>
          );
        })}
      </ol>
    </nav>
  );
}

// Decision confirmation modal
function DecisionModal({
  isOpen,
  onClose,
  onConfirm,
  title,
  description,
  confirmLabel,
  variant = 'primary',
}: {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (note: string, conditions?: string[]) => void;
  title: string;
  description: string;
  confirmLabel: string;
  variant?: 'primary' | 'danger';
}) {
  const [note, setNote] = useState('');
  const [conditionsText, setConditionsText] = useState('');
  
  if (!isOpen) return null;
  
  const handleConfirm = () => {
    const conditions = conditionsText.trim() 
      ? conditionsText.split('\n').filter(c => c.trim())
      : undefined;
    onConfirm(note, conditions);
    setNote('');
    setConditionsText('');
    onClose();
  };
  
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
      <div className="bg-[var(--bg-raised)] shadow-xl max-w-md w-full p-6">
        <h3 className="text-lg font-semibold text-[var(--text-pri)] mb-2">{title}</h3>
        <p className="text-sm text-[var(--text-sec)] mb-4">{description}</p>
        
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-[var(--text-sec)] mb-1">
              Notes (optional)
            </label>
            <textarea
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="Add any notes or rationale..."
              className="w-full px-3 py-2 border border-[var(--rule)] text-sm"
              rows={3}
            />
          </div>
          
          {variant === 'primary' && (
            <div>
              <label className="block text-sm font-medium text-[var(--text-sec)] mb-1">
                Conditions (one per line, optional)
              </label>
              <textarea
                value={conditionsText}
                onChange={(e) => setConditionsText(e.target.value)}
                placeholder="Any conditions attached to this decision..."
                className="w-full px-3 py-2 border border-[var(--rule)] text-sm"
                rows={2}
              />
            </div>
          )}
        </div>
        
        <div className="flex justify-end gap-3 mt-6">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-[var(--text-sec)] bg-[var(--bg-raised)] border border-[var(--rule)] hover:bg-[var(--surface)]"
          >
            Cancel
          </button>
          <button
            onClick={handleConfirm}
            className={`px-4 py-2 text-sm font-medium text-white  ${
              variant === 'danger' 
                ? 'bg-[var(--coral)] hover:bg-[var(--coral)]' 
                : 'bg-[var(--slate)] hover:bg-[var(--slate)]'
            }`}
          >
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}

// Override item display
function OverrideCard({ override }: { override: PriorityOverride }) {
  const typeColors: Record<string, string> = {
    promote: 'bg-[var(--green-dim)] text-[var(--green)] dark:text-[var(--text-ter)]',
    demote: 'bg-[var(--mustard-dim)] text-[var(--mustard)]',
    add: 'bg-[var(--slate-dim)] text-[var(--text-sec)]',
    remove: 'bg-[var(--coral-dim)] text-[var(--coral)]',
    modify: 'bg-[var(--slate-dim)] text-[var(--text-sec)]',
    re_bucket: 'bg-[var(--slate-dim)] text-[var(--text-sec)]',
    tie_break: 'bg-[var(--hover-tint)] text-[var(--text-pri)]',
  };
  
  return (
    <div className="p-3 bg-[var(--surface)] border border-[var(--rule)]">
      <div className="flex items-center justify-between mb-2">
        <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${typeColors[override.type]}`}>
          {override.type.replace('_', ' ')}
        </span>
        <span className="text-xs text-[var(--text-ter)]">
          {new Date(override.overriddenAt).toLocaleDateString()}
        </span>
      </div>
      <p className="text-sm text-[var(--text-sec)]">{override.rationale}</p>
    </div>
  );
}

// Main component
export function PrioritySetGovernance() {
  const { cycleId } = useParams<{ cycleId: string }>();
  const userId = useUserStore(state => state.userId);
  
  // Select raw data from stores (stable references)
  const allPrioritySets = usePrioritySetStore(state => state.prioritySets);
  const decidePrioritySet = usePrioritySetStore(state => state.decidePrioritySet);
  const ratifyPrioritySet = usePrioritySetStore(state => state.ratifyPrioritySet);
  const publishPrioritySet = usePrioritySetStore(state => state.publishPrioritySet);
  const reopenPrioritySet = usePrioritySetStore(state => state.reopenPrioritySet);
  const loadPrioritySetsForCycle = usePrioritySetStore(state => state.loadPrioritySetsForCycle);
  
  const allCycles = useDegapProcessStore(state => state.cycles);
  const items = useItemsStore(state => state.items);
  
  // Derive filtered values with useMemo
  const prioritySets = useMemo(
    () => allPrioritySets
      .filter(s => s.cycleId === cycleId)
      .sort((a, b) => b.proposedAt.getTime() - a.proposedAt.getTime()),
    [allPrioritySets, cycleId]
  );
  
  const currentSet = useMemo(
    () => prioritySets.find(s => s.state !== 'superseded'),
    [prioritySets]
  );
  
  const cycle = useMemo(
    () => allCycles.find(c => c.id === cycleId),
    [allCycles, cycleId]
  );
  
  // Modal state
  const [activeModal, setActiveModal] = useState<'decide' | 'ratify' | 'publish' | 'reopen' | null>(null);

  // Last failed governance action, surfaced in the Actions card
  const [actionError, setActionError] = useState<string | null>(null);

  // Inversion acknowledgment gate: tracks whether all inversions in the ranked
  // set have been acknowledged. Defaults to true (no inversions = no gate).
  const [inversionsAllAcknowledged, setInversionsAllAcknowledged] = useState(true);

  // F4: the loop-side publish prerequisites (ball taken, loop closed, EGAp
  // iteration accepted) used to be discoverable only by clicking Publish and
  // reading the failure. Derive them up front — same reads the gate does.
  const setForGovernance = currentSet ?? prioritySets[0] ?? null;
  const ratification = useRatificationSteps(cycleId, setForGovernance?.loopId ?? null);

  // The call behind this list, drawn with its standing, and the acknowledged
  // inversions drawn on the rows they qualify (owner, 2026-10-30: a standing is
  // never drawn alone). The decision comes from the workspace's decision record;
  // the list→decision link lives on the ratification or publication event.
  const decisions = useDecisionsStore((s) => s.decisions);
  const fetchDecisions = useDecisionsStore((s) => s.fetchDecisions);
  const [decisionsReady, setDecisionsReady] = useState(decisions.length > 0);
  const [listDecisionIds, setListDecisionIds] = useState<ReadonlyMap<string, string> | null>(null);
  const [acknowledgments, setAcknowledgments] = useState<InversionAcknowledgment[] | null>(null);
  const setIdForRecord = setForGovernance?.id ?? null;
  const setStateForRecord = setForGovernance?.state ?? null;

  useEffect(() => {
    if (decisions.length > 0) {
      setDecisionsReady(true);
      return;
    }
    let cancelled = false;
    void fetchDecisions().finally(() => {
      if (!cancelled) setDecisionsReady(true);
    });
    return () => {
      cancelled = true;
    };
  }, [decisions.length, fetchDecisions]);

  useEffect(() => {
    if (!setIdForRecord || !isAgreedListState(setStateForRecord)) {
      setListDecisionIds(new Map());
      return;
    }
    let cancelled = false;
    setListDecisionIds(null);
    fetchListApprovalDecisionIds([setIdForRecord])
      .then((m) => {
        if (!cancelled) setListDecisionIds(m);
      })
      .catch(() => {
        if (!cancelled) setListDecisionIds(new Map());
      });
    return () => {
      cancelled = true;
    };
  }, [setIdForRecord, setStateForRecord]);

  // Re-read after an acknowledgment is written below, so the row shows it at once.
  useEffect(() => {
    if (!setIdForRecord || !isAgreedListState(setStateForRecord)) {
      setAcknowledgments([]);
      return;
    }
    let cancelled = false;
    loadInversionAcknowledgments(setIdForRecord)
      .then((rows) => {
        if (!cancelled) setAcknowledgments(rows);
      })
      .catch(() => {
        if (!cancelled) setAcknowledgments([]);
      });
    return () => {
      cancelled = true;
    };
  }, [setIdForRecord, setStateForRecord, inversionsAllAcknowledged]);

  const acknowledgedByItem = useMemo(() => acknowledgedInversionsByHigherItem(acknowledgments ?? []), [acknowledgments]);
  const acknowledgerIds = useMemo(() => (acknowledgments ?? []).map((a) => a.acknowledgedBy), [acknowledgments]);
  const acknowledgerNames = useActorDisplayMap(acknowledgerIds);
  const listCall = useMemo(
    () => (setForGovernance && decisionsReady && listDecisionIds ? decisionForList(setForGovernance, decisions, listDecisionIds) : null),
    [setForGovernance, decisionsReady, listDecisionIds, decisions],
  );

  // Contest (migration 969): who did not agree, read beside who agreed.
  const contests = usePriorityListContests(setForGovernance?.id, setForGovernance?.state, userId);
  const [contestOpen, setContestOpen] = useState(false);

  // D-4 (migration 987): who may ratify is the database's answer — today's
  // rule, then the cycle's "Items / selection" decision right when one is set.
  // Ratify is drawn only for a person who may use it; otherwise a plain line
  // says what it takes and who holds it (build contract §2.8).
  const ratifyRight = usePriorityListRatifyRight(
    setForGovernance?.state === 'decided' ? setForGovernance.id : null,
  );
  
  // This cycle's lists are read here, for this cycle, not left to the layout's
  // workspace-wide hydration. Seen on production 2026-11-02 (Backspace demo,
  // click 7): the page said "No Priority Lists" for 25 to 40 seconds while
  // that hydration ran, then filled in. Until the read answers, the page says
  // it is reading; "No Priority Lists" is only ever the record's answer.
  const [listsRead, setListsRead] = useState(false);
  useEffect(() => {
    if (!cycleId) {
      setListsRead(true);
      return;
    }
    let live = true;
    setListsRead(false);
    void loadPrioritySetsForCycle(cycleId).finally(() => {
      if (live) setListsRead(true);
    });
    return () => {
      live = false;
    };
  }, [cycleId, loadPrioritySetsForCycle]);

  if (!currentSet && prioritySets.length === 0 && !listsRead) {
    return (
      <div className="max-w-screen-2xl mx-auto px-6 py-8">
        <div
          className="text-center py-12 bg-[var(--bg-raised)] border border-[var(--rule)]"
          data-testid="priority-list-governance-reading"
        >
          <p className="text-sm text-[var(--text-ter)]">Reading the record…</p>
        </div>
      </div>
    );
  }

  // No sets yet
  if (!currentSet && prioritySets.length === 0) {
    return (
      <div className="max-w-screen-2xl mx-auto px-6 py-8">
        <div className="text-center py-12 bg-[var(--bg-raised)] border border-[var(--rule)]">
          <FileText className="mx-auto h-12 w-12 text-[var(--text-ter)]" />
          <h3 className="mt-4 text-lg font-medium text-[var(--text-pri)]">No Priority Lists</h3>
          <p className="mt-2 text-sm text-[var(--text-ter)] max-w-md mx-auto">
            Priority Lists are created when a Catchball loop completes and produces an output.
            Start a Catchball loop to align priorities.
          </p>
          <Link
            to={`/prioritization/cycles/${cycleId}`}
            className="mt-6 inline-flex items-center gap-2 px-4 py-2 bg-[var(--slate)] text-white hover:bg-[var(--slate)]"
          >
            <ArrowRight className="h-4 w-4" />
            Go to Cycle
          </Link>
        </div>
      </div>
    );
  }
  
  const activeSet = currentSet || prioritySets[0];
  const planningEnabled = getPlanningEnablement(activeSet.state);
  const listIsContestable = isContestableListState(activeSet.state);
  // The section is drawn while the list can be contested, and afterwards for as
  // long as it holds any contest (a superseded list keeps its record).
  const showContestSection = listIsContestable || (contests.contests?.length ?? 0) > 0;
  const didNotAgree = didNotAgreeWords(contests.openContests.length);
  const agreedName = (id: string | undefined, name: string | undefined) =>
    name?.trim() || (id && id !== 'unknown' ? contests.nameOf(id) : 'someone not on record');
  
  // Get items in this set
  const setItems = items.filter(c => activeSet.rankedItemIds.includes(c.id));
  
  // Actions based on current state. Failures (state-machine guards, publish
  // gates like unacknowledged inversions) surface in the Actions card.
  const runAction = (action: Promise<{ success: boolean; error?: string }>) => {
    setActionError(null);
    void action.then(result => {
      if (!result.success) setActionError(result.error ?? 'Governance action failed');
    });
  };

  const handleDecide = (note: string, conditions?: string[]) => {
    if (!userId) return;
    runAction(decidePrioritySet(activeSet.id, userId, {
      note: note || undefined,
      conditions,
      confidence: 'high',
    }));
  };

  const handleRatify = (note: string) => {
    if (!userId) return;
    runAction(ratifyPrioritySet(activeSet.id, userId, { notes: note || undefined }));
  };

  const handlePublish = (note: string) => {
    if (!userId) return;
    runAction(publishPrioritySet(activeSet.id, userId, note || undefined));
  };

  const handleReopen = (note: string) => {
    if (!userId) return;
    runAction(reopenPrioritySet(activeSet.id, userId, note || undefined));
  };

  const publishBlockedReason = !inversionsAllAcknowledged
    ? 'Acknowledge all prioritization inversions before publishing'
    : ratification.publishBlockedReason;
  
  return (
    <div className="max-w-screen-2xl mx-auto px-6 py-8">
      {/* Breadcrumb */}
      <nav className="flex items-center gap-2 text-sm text-[var(--text-ter)] mb-6">
        <Link to="/prioritization/cycles" className="hover:text-[var(--text-sec)]">Cycles</Link>
        <ChevronRight className="h-4 w-4" />
        <Link to={`/prioritization/cycles/${cycleId}`} className="hover:text-[var(--text-sec)]">
          {cycle?.name || 'Cycle'}
        </Link>
        <ChevronRight className="h-4 w-4" />
        <span className="text-[var(--text-pri)]">Priority List Governance</span>
      </nav>
      
      {/* Header */}
      <div className="bg-[var(--bg-raised)] border border-[var(--rule)] p-6 mb-6">
        <div className="flex items-start justify-between mb-4">
          <div>
            <h1 className="text-2xl font-bold text-[var(--text-pri)]">{activeSet.name}</h1>
            {activeSet.description && (
              <p className="text-[var(--text-sec)] mt-1">{activeSet.description}</p>
            )}
            <StructureVersionFinePrint table="priority_sets" id={activeSet.id} mode="made" className="mt-1" />
          </div>
          <StateBadge state={activeSet.state} />
        </div>
        
        {/* Progress */}
        <div className="py-4 border-t border-[var(--rule)]">
          <GovernanceProgress currentState={activeSet.state} />
        </div>

        {/* Who agreed, and beside it who did not (Contest, migration 969) */}
        {(activeSet.ratification || activeSet.publication) && (
          <div className="py-3 border-t border-[var(--rule)] flex flex-wrap items-center gap-x-4 gap-y-1 text-sm">
            <span className="text-[var(--text-ter)]">Who agreed:</span>
            {activeSet.ratification && (
              <span className="text-[var(--text-sec)]">
                Ratified by {agreedName(activeSet.ratification.ratifiedBy, activeSet.ratification.ratifiedByName)} on{' '}
                {activeSet.ratification.ratifiedAt.toLocaleDateString()}
              </span>
            )}
            {activeSet.publication && (
              <span className="text-[var(--text-sec)]">
                Published by {agreedName(activeSet.publication.publishedBy, activeSet.publication.publishedByName)} on{' '}
                {activeSet.publication.publishedAt.toLocaleDateString()}
              </span>
            )}
            {showContestSection ? (
              <a
                href="#who-did-not-agree"
                className={contests.openContests.length > 0 ? 'text-[var(--coral)] hover:underline' : 'text-[var(--text-ter)] hover:underline'}
              >
                {didNotAgree}
              </a>
            ) : (
              <span className="text-[var(--text-ter)]">{didNotAgree}</span>
            )}
          </div>
        )}

        {/* The call behind this list: the standing is never drawn alone (owner, 2026-10-30) */}
        <div className="py-3 border-t border-[var(--rule)]" data-testid="list-call">
          {!listCall ? (
            <p className="text-sm text-[var(--text-ter)]">Reading the decision record…</p>
          ) : listCall.decision ? (
            <CallSentence compact eyebrow="The call behind this list" decision={listCall.decision} decisions={decisions} />
          ) : (
            <>
              <p className="text-xs font-medium uppercase tracking-wide text-[var(--text-ter)]">The call behind this list</p>
              <p className="mt-1 text-sm text-[var(--text-sec)]">{listDecisionGapText(activeSet.state)}</p>
            </>
          )}
        </div>
        
        {/* Planning Enablement Banner */}
        <div className={`mt-4 p-4  ${
          planningEnabled === 'authorized' ? 'bg-[var(--green-dim)] border border-[var(--green)]' :
          planningEnabled === 'locked' ? 'bg-[var(--slate-dim)] border border-[var(--slate)]' :
          'bg-[var(--mustard-dim)] border border-[var(--mustard)]'
        }`}>
          <div className="flex items-center gap-3">
            {planningEnabled === 'authorized' ? (
              <Unlock className="h-5 w-5 text-[var(--green)]" />
            ) : planningEnabled === 'locked' ? (
              <Lock className="h-5 w-5 text-[var(--text-ter)]" />
            ) : (
              <Clock className="h-5 w-5 text-[var(--mustard)]" />
            )}
            <div>
              <p className={`font-medium ${
                planningEnabled === 'authorized' ? 'text-[var(--green)] dark:text-[var(--text-ter)]' :
                planningEnabled === 'locked' ? 'text-[var(--text-ter)]' :
                'text-[var(--mustard)]'
              }`}>
                {PLANNING_ENABLEMENT_LABELS[planningEnabled]}
              </p>
              <p className={`text-sm ${
                planningEnabled === 'authorized' ? 'text-[var(--green)] dark:text-[var(--text-ter)]' :
                planningEnabled === 'locked' ? 'text-[var(--text-ter)]' :
                'text-[var(--mustard)]'
              }`}>
                {planningEnabled === 'authorized' 
                  ? 'Teams can begin planning work on these priorities.'
                  : planningEnabled === 'locked'
                  ? 'The planning baseline is locked. Changes require formal override.'
                  : 'Priorities are still being aligned. Planning should wait.'}
              </p>
            </div>
          </div>
        </div>
      </div>
      
      {/* Actions */}
      <div className="bg-[var(--bg-raised)] border border-[var(--rule)] p-6 mb-6">
        <h2 className="text-lg font-semibold text-[var(--text-pri)] mb-4">Actions</h2>

        {actionError && (
          <div className="mb-4 p-3 bg-[var(--coral-dim)] border border-[var(--coral)] text-sm text-[var(--coral)]">
            {actionError}
          </div>
        )}

        <div className="flex flex-wrap gap-3">
          {activeSet.state === 'proposed' && (
            <button
              onClick={() => setActiveModal('decide')}
              className="inline-flex items-center gap-2 px-4 py-2 bg-[var(--slate)] text-white hover:bg-[var(--slate)]"
            >
              <CheckCircle className="h-4 w-4" />
              Authorize planning
            </button>
          )}
          
          {activeSet.state === 'decided' && (
            <>
              {ratifyRight.allowed ? (
                <button
                  onClick={() => setActiveModal('ratify')}
                  className="inline-flex items-center gap-2 px-4 py-2 bg-[var(--slate)] text-white hover:bg-[var(--slate)]"
                >
                  <Lock className="h-4 w-4" />
                  Ratify (Final Lock)
                </button>
              ) : ratifyRight.line ? (
                <p className="self-center max-w-md text-sm text-[var(--text-ter)]">{ratifyRight.line}</p>
              ) : null}
              <button
                onClick={() => setActiveModal('reopen')}
                className="inline-flex items-center gap-2 px-4 py-2 text-[var(--text-sec)] bg-[var(--bg-raised)] border border-[var(--rule)] hover:bg-[var(--surface)]"
              >
                <RotateCcw className="h-4 w-4" />
                Reopen for Revision
              </button>
            </>
          )}
          
          {activeSet.state === 'ratified' && (
            <button
              onClick={() => setActiveModal('publish')}
              disabled={!inversionsAllAcknowledged || !ratification.canPublish}
              title={publishBlockedReason ?? undefined}
              className="inline-flex items-center gap-2 px-4 py-2 bg-[var(--slate)] text-white hover:bg-[var(--slate)] disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Send className="h-4 w-4" />
              Publish priorities
            </button>
          )}
          
          {activeSet.state === 'published' && (
            <div className="flex items-center gap-2 text-[var(--green)] dark:text-[var(--text-ter)] bg-[var(--green-dim)] px-4 py-2">
              <CheckCircle className="h-5 w-5" />
              <span className="font-medium">Published priorities</span>
              {contests.openContests.length > 0 && (
                <a href="#who-did-not-agree" className="text-sm text-[var(--coral)] hover:underline">
                  · {didNotAgree}
                </a>
              )}
            </div>
          )}

          {/* Contest: drawn only for a member of the list's workspace with no open contest on it (§2.8) */}
          {contests.mayContest && (
            <Button variant="secondary" onClick={() => setContestOpen(true)}>
              <Flag className="h-4 w-4" />
              Contest
            </Button>
          )}
        </div>

        {listIsContestable && contests.myOpenContest && (
          <p className="mt-3 text-sm text-[var(--text-ter)]">
            You contested this list on {new Date(contests.myOpenContest.contested_at ?? '').toLocaleDateString()}.
          </p>
        )}
      </div>

      {/* F4: the remaining path to Plan of Record, above Publish rather than
          behind it — the gates are unchanged, only the wayfinding is new. */}
      {activeSet.state === 'ratified' && activeSet.loopId && (
        <div className="mb-6">
          <RatificationNextSteps
            cycleId={cycleId!}
            loopId={activeSet.loopId}
            title="Before this can be published"
            hideWhenComplete={false}
          />
        </div>
      )}

      {/* Priority Items */}
      <div className="bg-[var(--bg-raised)] border border-[var(--rule)] p-6 mb-6">
        <h2 className="text-lg font-semibold text-[var(--text-pri)] mb-4">
          Priorities ({setItems.length})
          {activeSet.cutLinePosition && (
            <span className="ml-2 text-sm font-normal text-[var(--text-ter)]">
              • Cut line at position {activeSet.cutLinePosition}
            </span>
          )}
        </h2>
        
        <div className="space-y-2">
          {activeSet.rankedItemIds.map((itemId, index) => {
            const item = items.find(c => c.id === itemId);
            if (!item) return null;
            
            const isAboveLine = activeSet.cutLinePosition 
              ? index < activeSet.cutLinePosition 
              : true;
            
            return (
              <div
                key={itemId}
                className={`flex items-center gap-3 p-3  border ${
                  isAboveLine 
                    ? 'bg-[var(--green-dim)] border-[var(--green)]' 
                    : 'bg-[var(--surface)] border-[var(--rule)]'
                }`}
              >
                <span className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                  isAboveLine ? 'bg-[var(--green-dim)] text-[var(--green)] dark:text-[var(--text-ter)]' : 'bg-[var(--hover-tint)] text-[var(--text-sec)]'
                }`}>
                  {index + 1}
                </span>
                <div className="flex-1">
                  <p className="font-medium text-[var(--text-pri)]">{item.title} <ItemRelationshipFlags itemId={item.id} className="ml-1" /></p>
                  {item.description && (
                    <p className="text-sm text-[var(--text-ter)] line-clamp-1">{item.description}</p>
                  )}
                  {(acknowledgedByItem.get(item.id) ?? []).map((a) => (
                    <InversionMark
                      key={a.id}
                      acknowledgment={a}
                      lowerTitle={items.find((c) => c.id === a.lowerRankedItemId)?.title ?? null}
                      acknowledgedByName={acknowledgerNames[a.acknowledgedBy] ?? null}
                    />
                  ))}
                </div>
                {!isAboveLine && (
                  <span className="text-xs text-[var(--text-ter)]">Below line</span>
                )}
              </div>
            );
          })}
          
          {setItems.length === 0 && (
            <p className="text-[var(--text-ter)] text-sm text-center py-4">
              No items in this priority list.
            </p>
          )}
        </div>
      </div>
      
      {/* Prioritization Inversions — shown for ratified sets awaiting publish */}
      {(activeSet.state === 'ratified' || activeSet.state === 'published') && userId && (
        <InversionAcknowledgmentPanel
          prioritySetId={activeSet.id}
          rankedItemIds={activeSet.rankedItemIds}
          items={items}
          actorId={userId}
          onAcknowledgmentChange={setInversionsAllAcknowledged}
        />
      )}

      {/* Who did not agree (Contest, migration 969) */}
      {showContestSection && <WhoDidNotAgreeSection state={contests} userId={userId} />}

      {/* Overrides (if any) */}
      {activeSet.ratification?.overrides && activeSet.ratification.overrides.length > 0 && (
        <div className="bg-[var(--bg-raised)] border border-[var(--rule)] p-6 mb-6">
          <h2 className="text-lg font-semibold text-[var(--text-pri)] mb-4">
            Overrides ({activeSet.ratification.overrides.length})
          </h2>
          <div className="space-y-3">
            {activeSet.ratification.overrides.map((override) => (
              <OverrideCard key={override.id} override={override} />
            ))}
          </div>
        </div>
      )}
      
      {/* Governance History */}
      <div className="bg-[var(--bg-raised)] border border-[var(--rule)] p-6">
        <h2 className="text-lg font-semibold text-[var(--text-pri)] mb-4">Governance History</h2>
        
        <div className="space-y-3">
          {activeSet.events.map((event) => (
            <div key={event.id} className="flex items-start gap-3 p-3 bg-[var(--surface)]">
              <div className="w-8 h-8 rounded-full bg-[var(--slate-dim)] flex items-center justify-center">
                <FileText className="h-4 w-4 text-[var(--text-ter)]" />
              </div>
              <div className="flex-1">
                <p className="font-medium text-[var(--text-pri)]">{event.type}</p>
                {event.note && (
                  <p className="text-sm text-[var(--text-sec)]">{event.note}</p>
                )}
                <p className="text-xs text-[var(--text-ter)] mt-1">
                  {new Date(event.occurredAt).toLocaleString()}
                </p>
              </div>
            </div>
          ))}
          
          {activeSet.events.length === 0 && (
            <p className="text-[var(--text-ter)] text-sm text-center py-4">
              No governance events yet.
            </p>
          )}
        </div>
      </div>
      
      {/* Modals */}
      <DecisionModal
        isOpen={activeModal === 'decide'}
        onClose={() => setActiveModal(null)}
        onConfirm={handleDecide}
        title="Authorize planning"
        description="Authorizing gives teams the green light to begin planning work on these priorities."
        confirmLabel="Authorize"
      />
      
      <DecisionModal
        isOpen={activeModal === 'ratify'}
        onClose={() => setActiveModal(null)}
        onConfirm={handleRatify}
        title="Ratify - Final Lock"
        description="Ratifying locks this priority list as the final version. Any changes after this will require formal overrides with tracked rationale."
        confirmLabel="Ratify"
      />
      
      <DecisionModal
        isOpen={activeModal === 'publish'}
        onClose={() => setActiveModal(null)}
        onConfirm={handlePublish}
        title="Publish priorities"
        description="Publishing puts these priorities on record and notifies stakeholders. This creates the organization's committed priority list."
        confirmLabel="Publish"
      />
      
      {contestOpen && (
        <ContestListModal
          listId={activeSet.id}
          listName={activeSet.name}
          onClose={() => setContestOpen(false)}
          onDone={async () => {
            setContestOpen(false);
            await contests.reload();
          }}
        />
      )}

      <DecisionModal
        isOpen={activeModal === 'reopen'}
        onClose={() => setActiveModal(null)}
        onConfirm={handleReopen}
        title="Reopen for Revision"
        description="Reopening allows changes to be made before ratification. The set will return to Proposed state."
        confirmLabel="Reopen"
        variant="danger"
      />
    </div>
  );
}

export default PrioritySetGovernance;
